import React from 'react'
import cn from 'classnames'

import { nonEmbeddedAttachments } from 'util/changesets'
import Attachments from 'components/Attachments'
import { emptyArr } from 'util/arrays'
import metrics from 'util/metrics'

import QuotedBody from './QuotedBody'
import TextBody from './TextBody'
import SubsequentActions from './SubsequentActions'
import TrackingDetails from './TrackingDetails'
import Rating from './Rating'

import {
  shouldStartExpanded,
  handlePrintClick,
  getByline,
  getAvatarComponent,
  getAuthor,
} from './util'

class ExpandedMessageView extends React.Component {
  componentDidMount() {
    const { message } = this.props
    metrics.stopTimer(`changeset-load-${message.id}`)
  }

  shouldComponentUpdate(nextProps) {
    const { message } = this.props
    const { message: nextMessage } = nextProps
    const actionsWereCount = message.subsequentActions
      ? message.subsequentActions.length
      : 0
    const actionsAreCount = nextMessage.subsequentActions
      ? nextMessage.subsequentActions.length
      : 0

    const should =
      message.id !== nextMessage.id || actionsAreCount !== actionsWereCount
    return should
  }

  onPrintClick = e => {
    e.stopPropagation()
    handlePrintClick(e)
  }

  onCopyLinkClick = e => {
    const { message, doShowSnackbar } = this.props
    const { origin, pathname } = window.location
    const messageURL = `${origin}${pathname}#changeset-${message.id}`
    if (!navigator.clipboard)
      return doShowSnackbar('Could not copy a link to the message.')
    navigator.clipboard
      .writeText(messageURL)
      .then(() =>
        doShowSnackbar('Link to the message was copied to your clipboard')
      )
    e.stopPropagation()
    return null
  }

  onRawEmailClick = e => {
    const { doOpenRawEmailPage, message } = this.props
    const change = message.action.change
    e.stopPropagation()
    doOpenRawEmailPage(change.id)
  }

  expandChangeset = () => {
    const { message, expand } = this.props
    expand(message.id)
  }

  render() {
    const {
      message,
      currentUserId,
      onOpenImage,
      isFirstMessage,
      mailboxIndicatorNeeded,
      mailboxName,
      mailboxColor,
    } = this.props

    const subsequentActions = message.subsequentActions || emptyArr

    const action = message.action
    const hasAttachments = message.hasAttachments
    const isFromMerge = message.isFromMerge
    const isForward = message.isForward
    const isOutsideCommunication = message.isOutsideCommunication

    const change = action.change
    const isNote = message.isNote
    const datetime = action.datetime
    const isBodyEmpty = change.isBodyEmpty
    const filtered = nonEmbeddedAttachments(change)

    const ccs = change.cc && change.cc.length > 0 ? change.cc : null
    const bccs = change.bcc && change.bcc.length > 0 ? change.bcc : null
    const readReceipt = message.readReceipt
    const messageChangeId = message.action.change.id

    return (
      <div
        id={`changeset-${message.id}`}
        className={cn('changeset expanded', {
          forward: isOutsideCommunication,
        })}
      >
        <div
          className={cn('action', {
            message: !isNote,
            note: isNote,
            'fullstack-noteAction': isNote,
          })}
        >
          {getAvatarComponent({ action })}

          <div className="contents">
            <div className="header" onClick={this.expandChangeset}>
              {getByline({
                action,
                currentUserId,
                isNote,
                isFirstMessage,
                isForward,
              })}

              <div className="right-side">
                <div className="meta">
                  <div className="icons">
                    {hasAttachments && <div className="Icon Icon-attachment" />}
                    {isFromMerge && <div className="Icon Icon-merge" />}
                  </div>
                  <div className="datetime">{datetime}</div>
                </div>
                <div className="actions hidden-in-print">
                  <a onClick={this.onPrintClick}>
                    <span className="Icon Icon-print" />
                  </a>
                  <a onClick={this.onCopyLinkClick}>
                    <span className="Icon Icon-link" />
                  </a>
                  {message.hasRawEmail && (
                    <a onClick={this.onRawEmailClick}>
                      <span className="Icon Icon-open-in-new" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            {!isNote &&
              mailboxIndicatorNeeded && (
                <div className="mailbox-indicator">
                  <div
                    className="dot"
                    style={{ backgroundColor: mailboxColor }}
                  />
                  <div className="name">{mailboxName}</div>
                </div>
              )}
            {(ccs || bccs) && (
              <div className="cc-and-bcc">
                {ccs && (
                  <span>
                    cc:{' '}
                    {ccs
                      .map(
                        cc => (cc.name ? `${cc.name} <${cc.email}>` : cc.email)
                      )
                      .sort()
                      .join(', ')}{' '}
                  </span>
                )}
                {bccs && (
                  <span>
                    bcc:{' '}
                    {bccs
                      .map(
                        bcc =>
                          bcc.name ? `${bcc.name} <${bcc.email}>` : bcc.email
                      )
                      .sort()
                      .join(', ')}
                  </span>
                )}
              </div>
            )}
            {isBodyEmpty && (
              <div className="empty-body">No content in this message</div>
            )}
            {!isBodyEmpty &&
              change.parsedBody.map((part, index) => {
                if (part.type === 'quoted') {
                  const startExpanded = shouldStartExpanded(
                    isForward,
                    isFirstMessage,
                    change.parsedBody,
                    index
                  )

                  return (
                    <QuotedBody
                      // eslint-disable-next-line react/no-array-index-key
                      key={`quoted-${index}`}
                      text={part.text}
                      collapsed={!startExpanded}
                    />
                  )
                }
                return (
                  <TextBody
                    // eslint-disable-next-line react/no-array-index-key
                    key={`text-${index}`}
                    text={part.text}
                    onOpenImage={onOpenImage}
                  />
                )
              })}

            <Attachments
              attachments={isBodyEmpty ? change.attachments : filtered}
            />
          </div>
        </div>
        <div className="subsequent-list">
          {isFirstMessage && <TrackingDetails />}
          <Rating messageChangeId={messageChangeId} />
          {readReceipt && (
            <div className="action subsequent">
              <div className="contents">
                <div className="header">
                  <div className="author">
                    <div className="byline">
                      Read by {getAuthor(readReceipt, currentUserId)}
                    </div>
                  </div>

                  <div className="right-side">
                    <div className="datetime">{readReceipt.datetime}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <SubsequentActions
            actions={subsequentActions}
            currentUserId={currentUserId}
          />
        </div>
      </div>
    )
  }
}

export default ExpandedMessageView
